import React, { useEffect, useState, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  BackgroundVariant,
  Panel,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import apiService from '../../services/api';
import { ERDGraph, TableNode as TableNodeType } from '../../types';

interface ERDDiagramProps {
  dbConnectionId: string;
  schema?: string;
}

interface TableNodeData {
  label: string;
  table_name: string;
  schema_name?: string;
  column_count: number;
  primary_keys: string[];
  description?: string;
}

// Custom Table Node Component
const TableNode: React.FC<{ data: TableNodeData }> = ({ data }) => {
  return (
    <div className="bg-white border-2 border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-shadow min-w-[200px]">
      <div className="bg-blue-600 text-white px-4 py-2 rounded-t-md font-semibold">
        {data.schema_name ? `${data.schema_name}.${data.table_name}` : data.table_name}
      </div>
      <div className="px-4 py-2 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>{data.column_count} columns</span>
        </div>
        {data.primary_keys.length > 0 && (
          <div className="mt-1 text-xs text-gray-500">
            <span className="font-medium">PK:</span> {data.primary_keys.join(', ')}
          </div>
        )}
        {data.description && (
          <div className="mt-2 text-xs text-gray-500 italic border-t pt-2">
            {data.description}
          </div>
        )}
      </div>
    </div>
  );
};

const nodeTypes = {
  tableNode: TableNode,
};

// Auto-layout function using Dagre
const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, ranksep: 100, nodesep: 80 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 250, height: 120 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 125,
        y: nodeWithPosition.y - 60,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const ERDDiagram: React.FC<ERDDiagramProps> = ({ dbConnectionId, schema }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [erdData, setErdData] = useState<ERDGraph | null>(null);

  const loadERD = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getERDGraph(dbConnectionId, schema);
      setErdData(data);

      // Convert ERD nodes to ReactFlow nodes
      const reactFlowNodes: Node<TableNodeData>[] = data.nodes.map((node: TableNodeType) => ({
        id: node.id,
        type: 'tableNode',
        data: {
          label: node.table_name,
          table_name: node.table_name,
          schema_name: node.schema_name,
          column_count: node.column_count,
          primary_keys: node.primary_keys,
          description: node.description,
        },
        position: node.position || { x: 0, y: 0 },
      }));

      // Convert ERD edges to ReactFlow edges
      const reactFlowEdges: Edge[] = data.edges.map((edge, index) => {
        const sourceId = edge.source_schema
          ? `${edge.source_schema}.${edge.source_table}`
          : edge.source_table;
        const targetId = edge.target_schema
          ? `${edge.target_schema}.${edge.target_table}`
          : edge.target_table;

        return {
          id: edge.id || `edge-${index}`,
          source: sourceId,
          target: targetId,
          label: `${edge.source_column} → ${edge.target_column}`,
          type: 'smoothstep',
          animated: true,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
          },
          style: { stroke: '#6366f1', strokeWidth: 2 },
          labelStyle: { fill: '#6366f1', fontSize: 11, fontWeight: 500 },
          labelBgStyle: { fill: '#ffffff', fillOpacity: 0.8 },
        };
      });

      // Apply auto-layout
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        reactFlowNodes,
        reactFlowEdges,
        'TB'
      );

      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    } catch (err: any) {
      console.error('Failed to load ERD:', err);
      setError(err?.response?.data?.detail || 'Failed to load ERD diagram');
    } finally {
      setLoading(false);
    }
  }, [dbConnectionId, schema, setNodes, setEdges]);

  useEffect(() => {
    loadERD();
  }, [loadERD]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading ERD diagram...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px]">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">Failed to load ERD</h3>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <button
            onClick={loadERD}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!erdData || erdData.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px]">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No tables found</h3>
          <p className="mt-2 text-sm text-gray-600">
            Please scan the database tables first to generate the ERD diagram.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-gray-50" style={{ height: '600px' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.1}
        maxZoom={2}
        attributionPosition="bottom-left"
      >
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        <Controls />
        <MiniMap
          nodeColor={(node) => '#60a5fa'}
          maskColor="rgba(0, 0, 0, 0.1)"
          position="bottom-right"
        />
        <Panel position="top-left" className="bg-white shadow-md rounded-md p-3 border">
          <div className="text-sm">
            <div className="font-semibold text-gray-900 mb-1">ERD Diagram</div>
            <div className="text-gray-600 space-y-0.5">
              <div>{erdData.nodes.length} tables</div>
              <div>{erdData.edges.length} relationships</div>
            </div>
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
};

export default ERDDiagram;
